import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar, Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend,
  ChartOptions,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import DatePicker from '../DatePicker';
import { ChatDashboardTabProps } from '../../types';
import ChartCard from '../charts/ChartCard';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  getActiveUsers,
  getQuestionAnalysisCounts,
  getQuestionRelevance,
  getTopicsDistribution,
  QuestionAnalysisCountsDailyStat,
  TopicDistributionItem,
  RelevanceDailyStat,
  ActiveUserItem,
} from '../../utils/dashboardApi';
import {
  buildMessagesDataset,
  MessagesDataset,
} from '../../utils/messagesStats';
import {
  buildTopicsDataset,
  TopicsDataset,
} from '../../utils/topicsDistribution';
import {
  buildRelevanceDataset,
  RelevanceDataset,
} from '../../utils/questionRelevance';
import {
  buildActiveUsersList,
  ActiveUserEntry,
} from '../../utils/activeUsers';
import { buildCsvContent, downloadCsv } from '../../utils/exportUtils';
import {
  buildMessagesCsvSpec,
  buildTopicsCsvSpec,
  buildRelevanceCsvSpec,
  buildActiveUsersCsvSpec,
} from '../../utils/exportConfigs';
import { DASHBOARD_CARD_TOOLTIPS } from '../../utils/tooltips';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ChartTooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement
);

const TOPICS_CHART_DISPLAY_LIMIT = 5;
const TOPICS_REQUEST_LIMIT = 17;
const DEFAULT_ACTIVE_USERS_LIMIT = 20;

const OverviewTab: React.FC<ChatDashboardTabProps> = ({ dateRange, onDateRangeChange }) => {
  const [messagesData, setMessagesData] = useState<MessagesDataset>({ labels: [], values: [] });
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState<string | null>(null);
  const [topicsData, setTopicsData] = useState<TopicsDataset>({ labels: [], values: [], colors: [], sdgNumbers: [] });
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [topicsError, setTopicsError] = useState<string | null>(null);
  const [relevanceData, setRelevanceData] = useState<RelevanceDataset>({ labels: [], values: [] });
  const [relevanceLoading, setRelevanceLoading] = useState(false);
  const [relevanceError, setRelevanceError] = useState<string | null>(null);
  const [activeUsers, setActiveUsers] = useState<ActiveUserEntry[]>([]);
  const [activeUsersLoading, setActiveUsersLoading] = useState(false);
  const [activeUsersError, setActiveUsersError] = useState<string | null>(null);
  const [rawMessagesStats, setRawMessagesStats] = useState<QuestionAnalysisCountsDailyStat[]>([]);
  const [rawTopicsStats, setRawTopicsStats] = useState<{
    topics: TopicDistributionItem[];
    othersCount: number;
  } | null>(null);
  const [rawRelevanceStats, setRawRelevanceStats] = useState<RelevanceDailyStat[]>([]);
  const [rawActiveUsers, setRawActiveUsers] = useState<ActiveUserItem[]>([]);

  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setMessagesData({ labels: [], values: [] });
      setMessagesLoading(false);
      setMessagesError('Please select a valid time range.');
      setRawMessagesStats([]);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchStats = async () => {
      setMessagesLoading(true);
      setMessagesError(null);

      try {
        const response = await getQuestionAnalysisCounts({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          signal: controller.signal,
        });

        if (!isMounted) return;

        const dailyStats = response.data.daily_stats ?? [];
        const dataset = buildMessagesDataset(
          dailyStats.map((stat) => ({
            date: stat.date,
            message_count: stat.total_messages ?? 0,
          })),
          dateRange.startDate,
          dateRange.endDate
        );
        setMessagesData(dataset);
        setRawMessagesStats(dailyStats);
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load message stats:', error);
        if (isMounted) {
          setMessagesError('Failed to load messages data.');
          setRawMessagesStats([]);
        }
      } finally {
        if (isMounted) {
          setMessagesLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [dateRange.endDate, dateRange.startDate]);

  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setTopicsData({ labels: [], values: [], colors: [], sdgNumbers: [] });
      setTopicsLoading(false);
      setTopicsError('Please select a valid time range.');
      setRawTopicsStats(null);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchTopics = async () => {
      setTopicsLoading(true);
      setTopicsError(null);

      try {
        const response = await getTopicsDistribution({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          limit: TOPICS_REQUEST_LIMIT,
          signal: controller.signal,
        });

        if (!isMounted) return;

        const sortedTopics = [...(response.data.topics ?? [])].sort(
          (a, b) => (b.message_count ?? 0) - (a.message_count ?? 0)
        );

        const topTopics = sortedTopics.slice(0, TOPICS_CHART_DISPLAY_LIMIT);
        const extraTopicsCount = sortedTopics
          .slice(TOPICS_CHART_DISPLAY_LIMIT)
          .reduce((sum, topic) => sum + (topic.message_count ?? 0), 0);
        const apiOthersCount = response.data.others?.message_count ?? 0;
        const combinedOthersCount = extraTopicsCount + apiOthersCount;

        const dataset = buildTopicsDataset(topTopics, combinedOthersCount);
        setTopicsData(dataset);
        setRawTopicsStats({
          topics: sortedTopics,
          othersCount: combinedOthersCount,
        });
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load topic distribution:', error);
        if (isMounted) {
          setTopicsError('Failed to load topics data.');
          setRawTopicsStats(null);
        }
      } finally {
        if (isMounted) {
          setTopicsLoading(false);
        }
      }
    };

    fetchTopics();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [dateRange.endDate, dateRange.startDate]);

  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setRelevanceData({ labels: [], values: [] });
      setRelevanceLoading(false);
      setRelevanceError('Please select a valid time range.');
      setRawRelevanceStats([]);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchRelevance = async () => {
      setRelevanceLoading(true);
      setRelevanceError(null);

      try {
        const response = await getQuestionRelevance({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          signal: controller.signal,
        });

        if (!isMounted) return;

        const dataset = buildRelevanceDataset(
          response.data.daily_stats,
          dateRange.startDate,
          dateRange.endDate
        );
        setRelevanceData(dataset);
        setRawRelevanceStats(response.data.daily_stats ?? []);
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load relevance data:', error);
        if (isMounted) {
          setRelevanceError('Failed to load relevance data.');
          setRawRelevanceStats([]);
        }
      } finally {
        if (isMounted) {
          setRelevanceLoading(false);
        }
      }
    };

    fetchRelevance();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [dateRange.endDate, dateRange.startDate]);

  useEffect(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      setActiveUsers([]);
      setActiveUsersLoading(false);
      setActiveUsersError('Please select a valid time range.');
      setRawActiveUsers([]);
      return;
    }

    let isMounted = true;
    const controller = new AbortController();

    const fetchActiveUsers = async () => {
      setActiveUsersLoading(true);
      setActiveUsersError(null);

      try {
        const response = await getActiveUsers({
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          limit: DEFAULT_ACTIVE_USERS_LIMIT,
          signal: controller.signal,
        });

        if (!isMounted) return;

        const users = buildActiveUsersList(response.data.users);
        setActiveUsers(users);
        setRawActiveUsers(response.data.users ?? []);
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') {
          return;
        }
        console.error('Failed to load active users:', error);
        if (isMounted) {
          setActiveUsersError('Failed to load active users.');
          setRawActiveUsers([]);
        }
      } finally {
        if (isMounted) {
          setActiveUsersLoading(false);
        }
      }
    };

    fetchActiveUsers();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [dateRange.endDate, dateRange.startDate]);

  const barChartData = useMemo(
    () => ({
      labels: messagesData.labels,
      datasets: [
        {
          label: 'Messages',
          data: messagesData.values,
          backgroundColor: '#3b82f6',
          borderRadius: 6,
        },
      ],
    }),
    [messagesData]
  );

  const barChartOptions = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = typeof context.parsed === 'number'
                ? context.parsed
                : context.parsed?.y;
              return `${value ?? 0} messages`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: { color: '#64748b' },
          grid: { display: false },
        },
        y: {
          ticks: { color: '#64748b' },
          grid: { color: 'rgba(148, 163, 184, 0.2)' },
        },
      },
    }),
    []
  );

  const pieChartData = useMemo(
    () => ({
      labels: topicsData.labels,
      datasets: [
        {
          data: topicsData.values,
          backgroundColor: topicsData.colors,
          borderWidth: 0,
        },
      ],
    }),
    [topicsData]
  );

  const pieChartOptions = useMemo<ChartOptions<'pie'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
      },
    }),
    []
  );

  const lineChartData = useMemo(
    () => ({
      labels: relevanceData.labels,
      datasets: [
        {
          label: 'Relevance',
          data: relevanceData.values,
          borderColor: '#2563eb',
          backgroundColor: 'rgba(37, 99, 235, 0.15)',
          tension: 0.4,
          fill: true,
          pointRadius: 3,
          pointBackgroundColor: '#2563eb',
        },
      ],
    }),
    [relevanceData]
  );

  const lineChartOptions = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = typeof context.parsed === 'number'
                ? context.parsed
                : context.parsed?.y;
              return `${(value ?? 0).toFixed(1)}%`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: { color: '#64748b' },
          grid: { display: false },
        },
        y: {
          ticks: { color: '#64748b', callback: (value) => `${value}%` },
          grid: { color: 'rgba(148, 163, 184, 0.2)' },
          min: 0,
          max: 100,
        },
      },
    }),
    []
  );

  const buildCsvFilename = useCallback(
    (prefix: string) => {
      const startLabel = (dateRange.startDate ?? 'start').replace(/:/g, '-');
      const endLabel = (dateRange.endDate ?? 'end').replace(/:/g, '-');
      return `${prefix}_${startLabel}_to_${endLabel}.csv`;
    },
    [dateRange.endDate, dateRange.startDate]
  );

  const canExportMessages = rawMessagesStats.length > 0;

  const handleExportMessages = useCallback(() => {
    if (!rawMessagesStats.length) {
      return;
    }
    const spec = buildMessagesCsvSpec(rawMessagesStats);
    const csv = buildCsvContent(spec.headers, spec.rows);
    downloadCsv(csv, buildCsvFilename('messages'));
  }, [rawMessagesStats, buildCsvFilename]);

  const messagesCardActions = useMemo(
    () => (
      <Tooltip title="Export to CSV">
        <span>
          <IconButton
            size="small"
            onClick={handleExportMessages}
            disabled={!canExportMessages}
          >
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    ),
    [canExportMessages, handleExportMessages]
  );

  const canExportTopics = Boolean(rawTopicsStats?.topics.length);
  const handleExportTopics = useCallback(() => {
    if (!rawTopicsStats || !rawTopicsStats.topics.length) {
      return;
    }
    const spec = buildTopicsCsvSpec(rawTopicsStats.topics);
    const csv = buildCsvContent(spec.headers, spec.rows);
    downloadCsv(csv, buildCsvFilename('topics'));
  }, [rawTopicsStats, buildCsvFilename]);
  const topicsCardActions = useMemo(
    () => (
      <Tooltip title="Export to CSV">
        <span>
          <IconButton
            size="small"
            onClick={handleExportTopics}
            disabled={!canExportTopics}
          >
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    ),
    [canExportTopics, handleExportTopics]
  );

  const canExportRelevance = rawRelevanceStats.length > 0;
  const handleExportRelevance = useCallback(() => {
    if (!rawRelevanceStats.length) {
      return;
    }
    const spec = buildRelevanceCsvSpec(rawRelevanceStats);
    const csv = buildCsvContent(spec.headers, spec.rows);
    downloadCsv(csv, buildCsvFilename('relevance'));
  }, [rawRelevanceStats, buildCsvFilename]);
  const relevanceCardActions = useMemo(
    () => (
      <Tooltip title="Export to CSV">
        <span>
          <IconButton
            size="small"
            onClick={handleExportRelevance}
            disabled={!canExportRelevance}
          >
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    ),
    [canExportRelevance, handleExportRelevance]
  );

  const canExportActiveUsers = rawActiveUsers.length > 0;
  const handleExportActiveUsers = useCallback(() => {
    if (!rawActiveUsers.length) {
      return;
    }
    const spec = buildActiveUsersCsvSpec(rawActiveUsers);
    const csv = buildCsvContent(spec.headers, spec.rows);
    downloadCsv(csv, buildCsvFilename('active_users'));
  }, [rawActiveUsers, buildCsvFilename]);
  const activeUsersCardActions = useMemo(
    () => (
      <Tooltip title="Export to CSV">
        <span>
          <IconButton
            size="small"
            onClick={handleExportActiveUsers}
            disabled={!canExportActiveUsers}
          >
            <FileDownloadOutlinedIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    ),
    [canExportActiveUsers, handleExportActiveUsers]
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-col gap-3 border-b border-gray-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-3xl font-bold text-gray-900">Overview</h2>
        <div className="w-full sm:w-auto sm:min-w-[280px]">
          <DatePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={onDateRangeChange}
            className="w-full"
          />
        </div>
      </div>

      <div className="mt-6 grid flex-1 min-h-0 grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard
          title="Messages"
          actions={messagesCardActions}
          tooltip={DASHBOARD_CARD_TOOLTIPS.messages}
        >
          {messagesLoading && (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              Loading messages...
            </div>
          )}
          {!messagesLoading && messagesError && (
            <div className="flex h-full items-center justify-center text-sm text-red-500">
              {messagesError}
            </div>
          )}
          {!messagesLoading && !messagesError && messagesData.labels.length > 0 && (
            <Bar data={barChartData} options={barChartOptions} />
          )}
          {!messagesLoading && !messagesError && messagesData.labels.length === 0 && (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
              No data for the selected range.
            </div>
          )}
        </ChartCard>
        <ChartCard title="Topics" actions={topicsCardActions} tooltip={DASHBOARD_CARD_TOOLTIPS.topics}>
          {topicsLoading && (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              Loading topics...
            </div>
          )}
          {!topicsLoading && topicsError && (
            <div className="flex h-full items-center justify-center text-sm text-red-500">
              {topicsError}
            </div>
          )}
          {!topicsLoading && !topicsError && topicsData.labels.length > 0 && (
            <div className="flex h-full flex-col gap-6 md:flex-row md:items-center">
              <div className="flex h-full items-center justify-center md:w-1/2">
                <div className="min-h-[200px] w-full max-w-[260px]">
                  <Pie data={pieChartData} options={pieChartOptions} />
                </div>
              </div>
              <div className="flex w-full flex-col justify-center space-y-3 text-sm md:w-1/2">
                {renderTopicLegend(topicsData)}
              </div>
            </div>
          )}
          {!topicsLoading && !topicsError && topicsData.labels.length === 0 && (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
              No topics data for the selected range.
            </div>
          )}
        </ChartCard>
        <ChartCard
          title="Question Relevance Analysis"
          actions={relevanceCardActions}
          tooltip={DASHBOARD_CARD_TOOLTIPS.questionRelevance}
        >
          {relevanceLoading && (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              Loading relevance data...
            </div>
          )}
          {!relevanceLoading && relevanceError && (
            <div className="flex h-full items-center justify-center text-sm text-red-500">
              {relevanceError}
            </div>
          )}
          {!relevanceLoading && !relevanceError && relevanceData.labels.length > 0 && (
            <Line data={lineChartData} options={lineChartOptions} />
          )}
          {!relevanceLoading && !relevanceError && relevanceData.labels.length === 0 && (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
              No relevance data for the selected range.
            </div>
          )}
        </ChartCard>
        <ChartCard
          title="Active Users"
          scrollable
          actions={activeUsersCardActions}
          tooltip={DASHBOARD_CARD_TOOLTIPS.activeUsers}
        >
          {activeUsersLoading && (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              Loading active users...
            </div>
          )}
          {!activeUsersLoading && activeUsersError && (
            <div className="flex h-full items-center justify-center text-sm text-red-500">
              {activeUsersError}
            </div>
          )}
          {!activeUsersLoading && !activeUsersError && activeUsers.length === 0 && (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
              No active users for the selected range.
            </div>
          )}
          {!activeUsersLoading && !activeUsersError && activeUsers.length > 0 && (
            <div className="flex h-full min-h-0 flex-col">
              <div className="flex items-center px-1 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <span className="w-12">#</span>
                <span className="flex-1">User</span>
                <span className="w-24 text-right">Messages</span>
              </div>
              <div
                className="custom-scrollbar flex-1 min-h-0 overflow-y-auto pr-2"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#cbd5e1 #f1f5f9'
                }}
              >
                {activeUsers.map((user) => (
                  <div
                    key={`${user.id}-${user.username}`}
                    className="flex items-center border-t border-gray-100 px-1 py-2 text-sm text-gray-700"
                  >
                    <span className="w-12 font-semibold text-gray-900">#{user.rank}</span>
                    <Tooltip title={`User ID: ${user.id}`} arrow>
                      <span className="flex-1 truncate cursor-default font-medium text-gray-900">
                        {user.username}
                      </span>
                    </Tooltip>
                    <span className="w-24 text-right font-mono font-semibold text-gray-900">
                      {formatCount(user.messageCount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
};

const renderTopicLegend = (dataset: TopicsDataset) => {
  if (!dataset.labels.length) {
    return <span className="text-gray-400">No data</span>;
  }

  return dataset.labels.map((label, idx) => {
    const sdgNumber = dataset.sdgNumbers?.[idx] ?? null;
    const numberedLabel = formatTopicLabel(label, sdgNumber);
    const displayLabel = truncateTopicLabel(numberedLabel);

    return (
      <div key={`${label}-${idx}`} className="flex items-center gap-3">
        <span
          className="h-3 w-3 rounded-full"
          style={{ backgroundColor: dataset.colors[idx] }}
        />
        <div className="flex w-full items-center justify-between gap-4">
          <Tooltip title={numberedLabel} arrow>
            <span className="flex-1 truncate text-gray-700 cursor-default">
              {displayLabel}
            </span>
          </Tooltip>
          <span className="w-16 text-right font-mono font-semibold text-gray-900">
            {formatCount(dataset.values[idx])}
          </span>
        </div>
      </div>
    );
  });
};

const formatTopicLabel = (label: string, sdgNumber: number | null) => {
  const normalized = label && label.trim().length > 0 ? label : 'Unknown';
  if (typeof sdgNumber === 'number' && !Number.isNaN(sdgNumber)) {
    return `${sdgNumber}. ${normalized}`;
  }
  return normalized;
};

const truncateTopicLabel = (label: string, limit: number = 30) => {
  if (label.length <= limit) {
    return label;
  }
  return `${label.slice(0, limit)}...`;
};

const formatCount = (value: number) => value.toLocaleString();

export default OverviewTab;
